
import { X, Calendar, Volume2, Radio, Mic, Camera, FileText } from 'lucide-react';
import { SonoReport } from '../../types/sonoReport';
import { format } from '../../utils/format';

interface SonoReportPreviewProps {
  report: SonoReport;
  onClose: () => void;
}

export default function SonoReportPreview({ report, onClose }: SonoReportPreviewProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return format.date(date);
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusStyle = (status: string) => {
      switch (status) {
        case 'OK':
        case 'OUI':
        case 'EFFECTUEE':
        case 'SATISFAISANT':
        case 'BONNE':
          return 'bg-green-100 text-green-800';
        case 'NOK':
        case 'NON':
        case 'NON_EFFECTUEE':
        case 'NON_SATISFAISANT':
        case 'PROBLEME':
        case 'MAUVAISE':
          return 'bg-red-100 text-red-800';
        case 'EN_COURS':
        case 'MOYENNE':
          return 'bg-yellow-100 text-yellow-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    const getDisplayText = (status: string) => {
      switch (status) {
        case 'EN_COURS': return 'En cours';
        case 'NON_EFFECTUEE': return 'Non effectuée';
        case 'NON_SATISFAISANT': return 'Non satisfaisant';
        default: return status;
      }
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(status)}`}>
        {getDisplayText(status)}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <Radio className="mr-2 text-[#00665C]" />
              Rapport Communication & Sono
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Date */}
          <div className="mb-8">
            <div className="flex items-center text-lg font-semibold text-gray-800 mb-2">
              <Calendar className="w-5 h-5 mr-2 text-[#00665C]" />
              Culte du {formatDate(report.date)}
            </div>
          </div>

          {/* Rapport Content */}
          <div className="space-y-8">
            {/* Avant le culte */}
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                <Volume2 className="w-5 h-5 mr-2" />
                Avant le culte
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Vérification du matériel Sono</span>
                  <StatusBadge status={report.beforeService.materialCheck} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Test du volume et qualité sonore</span>
                  <StatusBadge status={report.beforeService.soundQualityTest} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Test du Live Streaming</span>
                  <StatusBadge status={report.beforeService.liveStreamingTest} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Test qualité son en ligne</span>
                  <StatusBadge status={report.beforeService.onlineSoundTest} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Test qualité vidéo en ligne</span>
                  <StatusBadge status={report.beforeService.onlineVideoTest} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Préparation matériel photo</span>
                  <StatusBadge status={report.beforeService.photoEquipmentPrep} />
                </div>
              </div>
            </div>

            {/* Pendant le culte */}
            <div className="bg-green-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
                <Mic className="w-5 h-5 mr-2" />
                Pendant le culte
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Lancement de la Proclamation</span>
                  <StatusBadge status={report.duringService.proclamationLaunch} />
                </div>
                {report.duringService.proclamationLaunchNotes && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-red-400">
                    <p className="text-sm text-gray-700">{report.duringService.proclamationLaunchNotes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Volume et qualité sonore dans la salle</span>
                  <StatusBadge status={report.duringService.roomSoundQuality} />
                </div>
                {report.duringService.roomSoundQualityNotes && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-red-400">
                    <p className="text-sm text-gray-700">{report.duringService.roomSoundQualityNotes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Diffusion en direct (Live Streaming)</span>
                  <StatusBadge status={report.duringService.liveStreaming} />
                </div>
                {report.duringService.liveStreamingNotes && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-blue-400">
                    <p className="text-sm text-gray-700">{report.duringService.liveStreamingNotes}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Qualité son en ligne</span>
                    <StatusBadge status={report.duringService.onlineSoundQuality} />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Qualité vidéo en ligne</span>
                    <StatusBadge status={report.duringService.onlineVideoQuality} />
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Prises de vue pendant le culte</span>
                  <StatusBadge status={report.duringService.photoshootDuringService} />
                </div>
                {report.duringService.photoshootDetails && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-green-400">
                    <p className="text-sm text-gray-700">{report.duringService.photoshootDetails}</p>
                  </div>
                )}

                {report.duringService.technicalProblems && (
                  <div className="p-4 bg-red-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-red-800 mb-2">Problèmes techniques rencontrés</h4>
                    <p className="text-sm text-red-700">{report.duringService.technicalProblems}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Après le culte */}
            <div className="bg-orange-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-orange-800 mb-4 flex items-center">
                <Camera className="w-5 h-5 mr-2" />
                Après le culte
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Prise de photos des serviteurs</span>
                  <StatusBadge status={report.afterService.servantsPhotos} />
                </div>
                {report.afterService.servantsPhotosNotes && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-orange-400">
                    <p className="text-sm text-gray-700">{report.afterService.servantsPhotosNotes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Application des masques sur les photos</span>
                  <StatusBadge status={report.afterService.photoMasking} />
                </div>
                {report.afterService.photoMaskingNotes && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-orange-400">
                    <p className="text-sm text-gray-700">{report.afterService.photoMaskingNotes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Diffusion des audios sur Replay</span>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={report.afterService.audioReplayPublication} />
                    {report.afterService.audioReplayTime && (
                      <span className="text-xs text-gray-600">à {report.afterService.audioReplayTime}</span>
                    )}
                  </div>
                </div>
                {report.afterService.audioReplayNotes && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-green-400">
                    <p className="text-sm text-gray-700">{report.afterService.audioReplayNotes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Diffusion des vidéos sur Replay</span>
                  <StatusBadge status={report.afterService.videoReplayPublication} />
                </div>
                {report.afterService.videoReplayNotes && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-yellow-400">
                    <p className="text-sm text-gray-700">{report.afterService.videoReplayNotes}</p>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Archivage et sauvegarde des fichiers</span>
                  <StatusBadge status={report.afterService.fileArchiving} />
                </div>
                {report.afterService.fileArchivingNotes && (
                  <div className="ml-4 p-3 bg-white rounded border-l-4 border-blue-400">
                    <p className="text-sm text-gray-700">{report.afterService.fileArchivingNotes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Observations générales */}
            {report.generalObservations && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Observation générale
                </h3>
                <div className="bg-white p-4 rounded-lg border-l-4 border-[#00665C]/50">
                  <p className="text-gray-700 whitespace-pre-wrap">{report.generalObservations}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}